import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import SimpleDataTable, { type TableColumn } from "./SimpleDataTable";
import { TodoTypes, type TodoItem } from "~/api/v2/useMyTodos";
import { dateDiff } from "~/helpers/date";
import {
  getTodoTypeText,
  getTodoIcon,
  getIconColor,
  getTodoDate,
  getTodoDateString,
  compareTodoOrder,
  getTodoNavigationPath,
} from "~/helpers/todo";
import { DueDateCell } from "~/molecules/DueDateCell";
import { useUserContext } from "~/hooks/useUserContext";
import AvatarWithFallback from "~/molecules/AvatarWithFallback";

interface Props {
  todos: TodoItem[];
  onClickRow?: (item: TodoItem) => void;
}

const TodoDataTable = ({ todos, onClickRow }: Props) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useUserContext();

  const columns: TableColumn<TodoItem>[] = [
    {
      header: t(($) => $.dashboard.column.todo),
      defaultSort: true,
      compareFn: compareTodoOrder,
      cell: (todo) => {
        const statusText = getTodoTypeText(todo, user.id);
        const Icon = getTodoIcon(todo);
        const iconColor = getIconColor(todo);

        return (
          <div className="flex items-center gap-2">
            {Icon && <Icon className={`size-4 ${iconColor}`} />}
            <p>{statusText}</p>
          </div>
        );
      },
    },
    {
      header: t(($) => $.dashboard.column.title),
      compareFn: (a, b) => a.title.localeCompare(b.title),
      cell: (item) => <span>{item.title}</span>,
    },
    {
      header: t(($) => $.dashboard.myTodos.dateLabels.date),
      compareFn: (a, b) => {
        return dateDiff(getTodoDate(a), getTodoDate(b));
      },
      cell: (todo: TodoItem) => {
        const dateString = getTodoDateString(todo);
        const isToRead = todo.todoType === TodoTypes.documentToRead;

        return dateString ? (
          <DueDateCell
            renderedDate={dateString}
            dueDate={getTodoDate(todo)}
            className={isToRead ? "text-foreground" : undefined}
            showTriangleAlert={!isToRead}
          />
        ) : null;
      },
    },
    {
      header: t(($) => $.commons.document.owner),
      compareFn: (a, b) => a.owner.firstName.localeCompare(b.owner.firstName),
      cell: (item) => (
        <AvatarWithFallback user={item.owner} size="xs" showTooltip />
      ),
    },
  ];

  return (
    <SimpleDataTable
      columns={columns}
      data={todos}
      onClickRow={(item) => {
        void navigate(getTodoNavigationPath(item));
        onClickRow?.(item);
      }}
    />
  );
};

export default TodoDataTable;
